import {
    EntityPanelFormGroup,
    EntityPanelGroupHeader,
} from '../../../panels/entity-panels/entity-panel-components';
import { FormSelect } from '../../../forms/integrated-form-controls/form-select';
import { FormTextarea } from '../../../forms/integrated-form-controls/form-text-area';
import {
    FormLocationSelect,
    LocationFormRowWrapper,
} from '../../../forms/integrated-form-controls/form-location-select';
import { FormDateInput } from '../../../forms/integrated-form-controls/form-date-input';
import React, { useCallback } from 'react';
import {
    AttributeOption,
    BoloPanelFormType,
    BoloPanelOptions,
    BoloTypeOption,
} from '@mark43/cad-api';
import { UseFormReturn } from 'react-hook-form';
import { Box, DataList, DataItem, FormGroup, ModeObject } from '@cad-arc';
import { ClassificationStrategy } from '../../../common/types';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { FormInput } from '../../../forms/integrated-form-controls/form-input';
import { addMinutes } from 'date-fns';
import { useBoloPanelMetadata } from '../state/bolo-panel-metadata-context';
import { observer } from 'mobx-react-lite';
import { TruncatedBoloLabels } from '../../bolo-queues/components/cells/bolo-labels-cell';

export const BoloFormInformationFields = observer(
    ({
        boloPanelOptions,
        form,
        editableMode,
    }: {
        boloPanelOptions?: BoloPanelOptions;
        form: UseFormReturn<BoloPanelFormType>;
        editableMode: ModeObject;
    }) => {
        const { subpremiseSupport, multiAgencySubdivisions } = useEnabledFeatures();
        const { control, setValue } = form;
        const metadata = useBoloPanelMetadata();
        const isNewBolo = !metadata?.boloId;

        const updatePriorityForBoloType = (boloType: BoloTypeOption) => {
            const currentBoloTypePriority = boloType.priorityAttributeId;
            const currentPriority = form.getValues('priority')?.value;
            const relatedPriority = boloPanelOptions?.priorityOptions?.find(
                (p) => p?.value === currentBoloTypePriority
            );

            if (relatedPriority && currentPriority !== relatedPriority?.value) {
                setValue('priority', relatedPriority);
            }
        };

        const updateExpirationDate = (boloType: BoloTypeOption | null) => {
            setValue(
                'scheduledExpirationDate',
                boloType?.autoExpireAfterMinutes
                    ? addMinutes(new Date(), boloType.autoExpireAfterMinutes).toISOString()
                    : null
            );
        };

        const onBoloTypeChange = () => {
            const currentBoloType = form.getValues('boloType');

            updateExpirationDate(currentBoloType);

            if (!currentBoloType) {
                return;
            }
            updatePriorityForBoloType(currentBoloType);
        };

        const boloLabelsRenderViewMode = useCallback(
            (value: AttributeOption[]) => <TruncatedBoloLabels labels={value} />,
            []
        );

        return (
            <EntityPanelFormGroup data-testid="bolo-information-fields" spacing={3}>
                <EntityPanelGroupHeader title="BOLO Information" />
                <FormSelect
                    length="md"
                    options={boloPanelOptions?.boloTypeOptions ?? []}
                    name="boloType"
                    control={control}
                    label="BOLO Type"
                    showRequiredIndicator
                    onChange={onBoloTypeChange}
                    editableProps={{ mode: editableMode.mode }}
                />
                <FormSelect
                    options={boloPanelOptions?.priorityOptions ?? []}
                    length="sm"
                    name="priority"
                    control={control}
                    label="Priority"
                    showRequiredIndicator
                />
                <FormTextarea name="description" control={control} label="Description" />
                {!isNewBolo && metadata && (
                    <Box pl={1}>
                        <DataList>
                            <DataItem dataKey="Primary Subject">
                                {metadata.primarySubjectDisplay}
                            </DataItem>
                            <DataItem dataKey="Primary Subject Notes">
                                {metadata.primarySubjectNotes}
                            </DataItem>
                        </DataList>
                    </Box>
                )}
                <FormGroup spacing={1}>
                    <LocationFormRowWrapper subpremisesEnabled={subpremiseSupport}>
                        <FormLocationSelect
                            control={control}
                            label="Location"
                            name="location"
                            length="lg"
                            asEditable={!isNewBolo ? !subpremiseSupport : false}
                            placeInputOutside={subpremiseSupport ? isNewBolo : !isNewBolo}
                            classificationStrategy={
                                multiAgencySubdivisions
                                    ? ClassificationStrategy.MULTI_AGENCY
                                    : ClassificationStrategy.SINGLE_AGENCY
                            }
                            eventNotificationName="EVENT_LOCATION"
                        />
                        {!subpremiseSupport && (
                            <FormInput
                                control={control}
                                label="Apt, Unit"
                                length="sm"
                                name="locationSubPremise"
                                eventNotificationName="EVENT_LOCATION_SUB_PREMISE"
                                asEditable={!isNewBolo}
                            />
                        )}
                    </LocationFormRowWrapper>
                </FormGroup>
                <FormDateInput
                    control={control}
                    name="scheduledExpirationDate"
                    label="Expiration Date/Time"
                    granularity="minute"
                    hasCalendar
                />
                <FormSelect
                    length="md"
                    options={boloPanelOptions?.labelOptions ?? []}
                    name="labels"
                    control={control}
                    label="Labels"
                    isMultiple
                    renderViewMode={boloLabelsRenderViewMode}
                />
            </EntityPanelFormGroup>
        );
    }
);
